// Counting-progress map for /electionday. Deliberately apolitical:
// a single neutral amber ramp instead of the partisan red/blue
// scale used by the paired result map. Pale at 0% counted, fully
// saturated at 100% — the encoding asks one question only ("how
// much of this district is in?"), so the visual language doesn't
// flirt with party colour at all.
//
// Same MAP_DATA.paths as EditorialDistrictMap → identical district
// geometry, identical dimensions, eye-to-eye comparison across the
// pair. Hover/tap surfaces % counted + a timestamp + a "see lead →"
// affordance that scrolls to and flashes the matching district on
// the result map.

import { useState, type CSSProperties } from 'react';
import { MAP_DATA, type BuiltPath } from '../../lib/buildMapPaths';
import { useElectionDayEditorial } from '../../lib/useElectionDayEditorial';
import type { DistrictEditorialResult } from '../../data/electionday-editorial-types';

interface CountProgressMapProps {
  /** Fires when the editor clicks the "see lead" affordance in a
   *  district's hover panel. The parent owns cross-map highlight
   *  state + scrolls the result map into view. */
  onSeeLead?: (districtId: number) => void;
}

export default function CountProgressMap({ onSeeLead }: CountProgressMapProps) {
  const { data } = useElectionDayEditorial();
  const [hovered, setHovered] = useState<number | null>(null);

  return (
    <section
      className="electionday-progress-map"
      aria-label="District count-progress map"
    >
      <header className="electionday-progress-map__head">
        <h3 className="electionday-progress-map__title">
          Count progress by district
        </h3>
        <Legend />
      </header>

      <p className="electionday-progress-map__lede">
        Apolitical view — how much of each district's count is in.
        Pale = untouched, dark = done.
      </p>

      <div className="electionday-progress-map__shell">
        <svg
          viewBox={MAP_DATA.viewBox}
          role="img"
          aria-label="Malta electoral districts, shaded by count progress"
          className="electionday-progress-map__svg"
        >
          <g>
            {MAP_DATA.paths.map((p) => {
              const ed = data.districts[`District ${p.id}`];
              const pct = ed?.count_progress_pct ?? 0;
              return (
                <PathTile
                  key={p.id}
                  path={p}
                  pct={pct}
                  hasEntry={!!ed && ed.count_progress_pct !== null}
                  onHover={setHovered}
                />
              );
            })}
          </g>

          <g pointerEvents="none">
            {MAP_DATA.paths.map((p) => (
              <g key={`l${p.id}`}>
                <circle
                  cx={p.labelX}
                  cy={p.labelY}
                  r={16}
                  fill="rgba(0,0,0,0.9)"
                />
                <text
                  x={p.labelX}
                  y={p.labelY}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontFamily="'Poppins', sans-serif"
                  fontSize="14"
                  fontWeight="800"
                  fill="#fff"
                >
                  {p.id}
                </text>
              </g>
            ))}
          </g>
        </svg>

        {hovered !== null && (
          <HoverTip
            districtId={hovered}
            editorial={data.districts[`District ${hovered}`]}
            onSeeLead={onSeeLead}
            onClose={() => setHovered(null)}
          />
        )}
      </div>
    </section>
  );
}

interface PathTileProps {
  path: BuiltPath;
  pct: number;
  hasEntry: boolean;
  onHover: (id: number | null) => void;
}

/** Amber alpha ramp. 0% → 0.08 (nearly invisible "untouched"),
 *  100% → 1.0 (fully saturated "done"). Linear between. Districts
 *  with no editorial entry at all render with the same pale fill
 *  as "0% counted" — the source-note line under the map flags this
 *  so the editor knows a missing entry isn't somehow rendering
 *  differently from an explicit 0. */
function PathTile({ path, pct, onHover }: PathTileProps) {
  const clamped = Math.max(0, Math.min(100, pct));
  const alpha = 0.08 + (clamped / 100) * 0.92;
  // Site-amber base — explicitly NOT red or blue, so the visual
  // language can never be mistaken for partisan lean.
  const fill = `rgba(201, 128, 18, ${alpha.toFixed(2)})`;
  return (
    <path
      d={path.path}
      fill={fill}
      stroke="#1f1f1f"
      strokeWidth={1.4}
      strokeLinejoin="round"
      strokeLinecap="round"
      vectorEffect="non-scaling-stroke"
      className="electionday-progress-map__district"
      onMouseEnter={() => onHover(path.id)}
      onMouseLeave={() => onHover(null)}
    />
  );
}

function Legend() {
  const swatch = (alpha: number): CSSProperties => ({
    background: `rgba(201, 128, 18, ${alpha})`,
    width: 14,
    height: 14,
    borderRadius: 3,
    border: '1px solid rgba(0,0,0,0.15)',
    display: 'inline-block',
    marginRight: 6,
    verticalAlign: 'middle',
  });
  return (
    <div className="electionday-progress-map__legend">
      <span>
        <span style={swatch(0.1)} />
        0%
      </span>
      <span>
        <span style={swatch(0.5)} />
        50%
      </span>
      <span>
        <span style={swatch(1)} />
        100%
      </span>
    </div>
  );
}

interface HoverTipProps {
  districtId: number;
  editorial: DistrictEditorialResult | undefined;
  onSeeLead?: (districtId: number) => void;
  onClose: () => void;
}

function HoverTip({
  districtId,
  editorial,
  onSeeLead,
  onClose,
}: HoverTipProps) {
  const pct = editorial?.count_progress_pct;
  const enteredAt = editorial?.enteredAt;
  return (
    <div
      className="electionday-progress-map__tip"
      onMouseLeave={onClose}
    >
      <strong>District {districtId}</strong>
      <p>
        Count:{' '}
        <strong>
          {pct === null || pct === undefined ? '—' : `${Math.round(pct)}%`}
        </strong>
        {pct != null && pct >= 100 && (
          <span className="electionday-progress-map__tip-done"> · done</span>
        )}
      </p>
      <p className="electionday-progress-map__tip-when">
        Last update: {enteredAt ? formatStamp(enteredAt) : 'never'}
      </p>
      {onSeeLead && (
        <button
          type="button"
          className="electionday-progress-map__tip-link"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onSeeLead(districtId)}
        >
          See lead on result map →
        </button>
      )}
    </div>
  );
}

function formatStamp(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
